import classNames from 'classnames/bind';
import React from 'react';

import styles from './Option.module.scss';
import Icon from '../Icon';

/** Properties interface of the option. */
export interface OptionProps extends React.LiHTMLAttributes<HTMLLIElement> {
  /**
   * Indicates that the option is selected.
   * @see [aria-selected](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-selected)
   */
  ariaSelected?: React.AriaAttributes['aria-selected'];

  /** Show a checkmark on selected option */
  checkSelected?: boolean;

  /** Child elements of the option. */
  children: React.ReactNode;

  /** Custom classname of the option. */
  className?: string;

  /** Indicates that the option has focus. */
  focused?: boolean;

  /** Unique identifier of the option. */
  id: string;

  /**
   * Callback method for when the option is clicked.
   * @param e - A mouse event.
   */
  onClick?: React.MouseEventHandler<HTMLLIElement>;

  /** Value to be submitted to a form. */
  value: string;
}

/** Default properties of the option. */
const defaultProps: Partial<OptionProps> = {
  ariaSelected: false,
};

/**
 * Classnames binding for the option.
 * @see [Classnames Usage](https://github.com/JedWatson/classnames#alternate-bind-version-for-css-modules)
 */
const cx = classNames.bind(styles);

/**
 * An option helper component.
 *
 * @see [W3C Listbox Pattern](https://www.w3.org/WAI/ARIA/apg/patterns/listbox/)
 *
 * @param props - Option properties.
 * @returns A JSX element.
 */
const Option = React.forwardRef<HTMLLIElement, OptionProps>((props, ref) => {
  const { ariaSelected, checkSelected, children, className, focused, id, onClick, value } = props;

  const classes = cx({ focused, option: true }, className);

  return (
    <li
      aria-selected={ariaSelected}
      className={classes}
      data-value={value}
      id={id}
      onClick={onClick}
      ref={ref}
      role="option"
      tabIndex={-1}
    >
      {children}
      {checkSelected && ariaSelected ? <Icon ariaHidden={true} variant="checkmark" /> : null}
    </li>
  );
});

Option.defaultProps = defaultProps;
Option.displayName = 'Option';

export default Option;
